import { PageTabs } from '@/components/PageTabs'
import { useActiveJobs } from '../hooks/useActiveJobs'
import { Suspense } from 'react'
import { PageLoading } from './PageLoading'
import React from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Tab,
  Typography,
  Paper,
  Chip,
  Stack,
} from '@mui/material'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import DashboardIcon from '@mui/icons-material/Dashboard'
import PeopleIcon from '@mui/icons-material/People'
import WorkIcon from '@mui/icons-material/Work'
import SettingsIcon from '@mui/icons-material/Settings'
import { Breadcrumbs } from './Breadcrumbs'
import { useTranslation } from 'react-i18next'

interface AdminTab {
  labelKey: string
  path: string
  icon: React.ReactElement
}

const adminTabs: AdminTab[] = [
  { labelKey: 'admin.overview', path: '/admin', icon: <DashboardIcon /> },
  { labelKey: 'admin.users', path: '/admin/users', icon: <PeopleIcon /> },
  { labelKey: 'admin.jobs', path: '/admin/jobs', icon: <WorkIcon /> },
  { labelKey: 'admin.settings', path: '/admin/settings', icon: <SettingsIcon /> },
]

export function AdminLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const runningJobsCount = useActiveJobs().filter(job => job.status === 'running').length

  // Determine active tab based on current path (no tab selected on /admin/gaps)
  const getActiveTab = (): number | false => {
    const path = location.pathname
    if (path === '/admin/gaps' || path.startsWith('/admin/gaps/')) {
      return false
    }

    // Check for exact match first
    const exactMatch = adminTabs.findIndex((tab) => tab.path === path)
    if (exactMatch !== -1) return exactMatch

    // Check for prefix match (e.g., /admin/users/123 matches /admin/users)
    for (let i = adminTabs.length - 1; i >= 0; i--) {
      if (path.startsWith(adminTabs[i].path) && adminTabs[i].path !== '/admin') {
        return i
      }
    }

    // Default to Overview
    return 0
  }

  const activeTab = getActiveTab()

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    navigate(adminTabs[newValue].path)
  }

  return (
    <Box>
      {/* Admin Header */}
      <Box data-mobile-redundant sx={{ mb: 3 }}>
        <Breadcrumbs />
        
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <AdminPanelSettingsIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h4" fontWeight={700}>
            {t('admin.title')}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          {t('admin.subtitle')}
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Paper
        sx={{
          mb: { xs: 1, sm: 3 },
          backgroundColor: 'background.paper',
          borderRadius: 2,
        }}
        elevation={0}
      >
        <PageTabs
          value={activeTab === false ? false : activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: { xs: 0, sm: 1 },
            '& .MuiTab-root': {
              minHeight: 56,
              textTransform: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          {adminTabs.map((tab) => (
            <Tab
              key={tab.path}
              icon={tab.icon}
              iconPosition="start"
              label={
                tab.path === '/admin/jobs' && runningJobsCount > 0 ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <span>{t(tab.labelKey)}</span>
                    <Chip
                      label={runningJobsCount}
                      size="small"
                      sx={{
                        height: 20,
                        minWidth: 20,
                        bgcolor: 'primary.main',
                        color: theme => theme.aperture.name === 'studio' ? theme.palette.primary.contrastText : 'white',
                        fontSize: '0.75rem',
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  </Stack>
                ) : (
                  t(tab.labelKey)
                )
              }
              sx={{
                gap: 1,
                '& .MuiSvgIcon-root': {
                  fontSize: 20,
                },
              }}
            />
          ))}
        </PageTabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        <Suspense fallback={<PageLoading />}><Outlet /></Suspense>
      </Box>
    </Box>
  )
}

